import React from 'react';
import { Box, Chip, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  Place,
  Directions,
  EditLocation,
  DirectionsCar,
  Explore,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { RootState } from '@/store';
import { TeamLogo } from '@/components/TeamLogo';
import { LeagueLogo } from '@/components/LeagueLogo';
import { PlayerAvatar } from '@/components/player/PlayerAvatar';
import { getWeatherIcon, getAqiColor } from '@/utils/weatherIcons';
import { convertTemp, tempUnitLabel } from '@/utils/temperatureUtils';
import { formatInUserTimezone } from '@/utils/timezoneHelpers';
import InlineMapPreview from '@/components/InlineMapPreview';
import { useSubscriptionTier } from '@/hooks/useSubscriptionTier';
import type { ScheduleMatch, MatchWeatherData } from '@/services/scheduleService';
import type { PlayerDepartureEntry } from '@/components/DepartureTimeDisplay';

// Severity colors per spec
const SEVERITY_COLORS = {
  light: { DIRECT: '#dc2626', MODERATE: '#d97706', MINIMAL: '#2563eb' },
  dark: { DIRECT: '#ef4444', MODERATE: '#f59e0b', MINIMAL: '#60a5fa' },
};


interface ScheduleMatchCardProps {
  match: ScheduleMatch;
  date: string;
  weather?: MatchWeatherData;
  weatherLoading?: boolean;
  departureEntries: PlayerDepartureEntry[];
  departureTimesLoading?: boolean;
  departureTimesPending?: boolean;
  conflictSeverity: string | null;
  conflictCount?: number;
  isPlayed: boolean;
  isInProgress: boolean;
  isMatchOver: boolean;
  borderColor: string;
  formatMatchTime: (dateTime: string | null, status?: string, match?: any) => string;
  onWeatherClick: (e: React.MouseEvent) => void;
  onVenueClick: (e: React.MouseEvent) => void;
  onConflictClick: (e: React.MouseEvent) => void;
  onSetAddress?: (e: React.MouseEvent) => void;
  onEditAddress?: (e: React.MouseEvent) => void;
  buildFullAddress: (match: any) => string;
}

/**
 * New match card component with 4-column desktop and stacked mobile layout.
 * Spec: match-card-ui-overhaul-spec.md
 */
const ScheduleMatchCard: React.FC<ScheduleMatchCardProps> = React.memo(({
  match,
  date,
  weather,
  weatherLoading,
  departureEntries,
  departureTimesLoading,
  departureTimesPending,
  conflictSeverity,
  conflictCount = 0,
  isPlayed,
  isInProgress,
  isMatchOver: matchOver,
  borderColor,
  formatMatchTime,
  onWeatherClick,
  onVenueClick,
  onConflictClick,
  onSetAddress,
  onEditAddress,
  buildFullAddress,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const temperatureUnit = useSelector((state: RootState) => state.auth.user?.temperatureUnit) || 'FAHRENHEIT';

  const hasConflict = !!conflictSeverity;
  const distUnit = useSelector((state: RootState) => state.auth.user?.distanceUnit) || 'MILES';
  const { hasFeature } = useSubscriptionTier();
  const hasWeather = !!(weather?.forecastAvailable && weather.shortForecast) && hasFeature('weather');
  const hasLocation = !!(match.address || match.city || match.state || (match.latitude && match.longitude));
  const fullAddress = buildFullAddress(match);
  const showWeatherSection = hasFeature('weather') && !matchOver && match.status !== 'COMPLETED' && match.status !== 'CANCELLED' && match.dateTime && match.status !== 'TBD';

  // Get primary departure entry for venue section
  const primaryDep = departureEntries.find(
    e => !e.departureInfo.noTravelRequired && e.departureInfo.departureTime !== null
  );

  const formatDistance = (meters?: number) => {
    if (!meters) return null;
    if (distUnit === 'KILOMETERS') {
      const km = meters / 1000;
      return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
    }
    const miles = meters / 1609.34;
    return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
  };

  const formatTravelTime = (minutes: number) => {
    if (minutes >= 60) {
      const h = Math.floor(minutes / 60);
      const m = Math.round(minutes % 60);
      return m > 0 ? `~${h}h ${m}m` : `~${h}h`;
    }
    return `~${Math.round(minutes)} min`;
  };

  const formatLeaveBy = (entry: typeof primaryDep) => {
    if (!entry?.departureInfo.departureTime) return null;
    const iso = dayjs(entry.departureInfo.departureTime).toISOString();
    return formatInUserTimezone(iso, 'h:mm A', false);
  };

  const hasDepartureFeature = hasFeature('departure_calculator');
  const leaveByStr = hasDepartureFeature ? formatLeaveBy(primaryDep) : null;
  const distanceStr = hasDepartureFeature && primaryDep ? formatDistance(primaryDep.departureInfo.distanceMeters) : null;
  const travelStr = hasDepartureFeature && primaryDep ? formatTravelTime(primaryDep.departureInfo.baseTravelTimeMinutes || primaryDep.departureInfo.travelTimeMinutes) : null;

  // Score display
  const hasScore = (match.status === 'COMPLETED' || match.status === 'IN_PROGRESS') && match.homeScore != null && match.awayScore != null;
  const homeLost = hasScore && match.homeScore! < match.awayScore!;
  const awayLost = hasScore && match.awayScore! < match.homeScore!;
  // Conflict severity mapping
  const badgeSeverity = conflictSeverity === 'DIRECT' ? 'DIRECT' as const
    : conflictSeverity === 'MODERATE' ? 'MODERATE' as const
    : 'MINIMAL' as const;
  const isDark = theme.palette.mode === 'dark';
  const conflictColor = hasConflict
    ? (isDark ? SEVERITY_COLORS.dark : SEVERITY_COLORS.light)[badgeSeverity]
    : undefined;

  // Weather icon for the glass pill / weather column
  const isDaytime = match.dateTime ? (() => { const h = new Date(match.dateTime!).getHours(); return h >= 6 && h < 18; })() : true;
  const weatherIconData = hasWeather ? getWeatherIcon(weather!.shortForecast!, isDaytime) : null;

  if (isMobile) {
    return (
      <MobileCard
        match={match}
        date={date}
        weather={weather}
        weatherLoading={weatherLoading}
        weatherIconData={weatherIconData}
        hasWeather={!!showWeatherSection && hasWeather}
        showWeatherSection={!!showWeatherSection}
        hasLocation={hasLocation}
        hasConflict={hasConflict}
        badgeSeverity={badgeSeverity}
        conflictCount={conflictCount}
        conflictColor={conflictColor}
        hasScore={hasScore}
        homeLost={homeLost}
        awayLost={awayLost}
        isPlayed={isPlayed}
        isInProgress={isInProgress}
        matchOver={matchOver}
        fullAddress={fullAddress}
        leaveByStr={leaveByStr}
        distanceStr={distanceStr}
        travelStr={travelStr}
        borderColor={borderColor}
        temperatureUnit={temperatureUnit}
        departureEntries={departureEntries}
        departureTimesLoading={!!departureTimesLoading}
        departureTimesPending={!!departureTimesPending}
        formatMatchTime={formatMatchTime}
        onWeatherClick={onWeatherClick}
        onVenueClick={onVenueClick}
        onConflictClick={onConflictClick}
        onSetAddress={onSetAddress}
        onEditAddress={onEditAddress}
      />
    );
  }

  return (
    <DesktopCard
      match={match}
      date={date}
      weather={weather}
      weatherLoading={weatherLoading}
      weatherIconData={weatherIconData}
      hasWeather={!!showWeatherSection && hasWeather}
      showWeatherSection={!!showWeatherSection}
      hasLocation={hasLocation}
      hasConflict={hasConflict}
      badgeSeverity={badgeSeverity}
      conflictCount={conflictCount}
      conflictColor={conflictColor}
      hasScore={hasScore}
      homeLost={homeLost}
      awayLost={awayLost}
      isPlayed={isPlayed}
      isInProgress={isInProgress}
      matchOver={matchOver}
      fullAddress={fullAddress}
      leaveByStr={leaveByStr}
      distanceStr={distanceStr}
      travelStr={travelStr}
      borderColor={borderColor}
      temperatureUnit={temperatureUnit}
      departureEntries={departureEntries}
      departureTimesLoading={!!departureTimesLoading}
      departureTimesPending={!!departureTimesPending}
      formatMatchTime={formatMatchTime}
      onWeatherClick={onWeatherClick}
      onVenueClick={onVenueClick}
      onConflictClick={onConflictClick}
      onSetAddress={onSetAddress}
      onEditAddress={onEditAddress}
    />
  );
});

ScheduleMatchCard.displayName = 'ScheduleMatchCard';

// ─── Shared types ───────────────────────────────────────────────────────

interface CardInnerProps {
  match: ScheduleMatch;
  date: string;
  weather?: MatchWeatherData;
  weatherLoading?: boolean;
  weatherIconData: { src: string } | null;
  hasWeather: boolean;
  showWeatherSection: boolean;
  hasLocation: boolean;
  hasConflict: boolean;
  badgeSeverity: 'DIRECT' | 'MODERATE' | 'MINIMAL';
  conflictCount: number;
  conflictColor?: string;
  hasScore: boolean;
  homeLost: boolean;
  awayLost: boolean;
  isPlayed: boolean;
  isInProgress: boolean;
  matchOver: boolean;
  fullAddress: string;
  leaveByStr: string | null;
  distanceStr: string | null;
  travelStr: string | null;
  borderColor: string;
  temperatureUnit: 'FAHRENHEIT' | 'CELSIUS';
  departureEntries: PlayerDepartureEntry[];
  departureTimesLoading: boolean;
  departureTimesPending: boolean;
  formatMatchTime: (dateTime: string | null, status?: string, match?: any) => string;
  onWeatherClick: (e: React.MouseEvent) => void;
  onVenueClick: (e: React.MouseEvent) => void;
  onConflictClick: (e: React.MouseEvent) => void;
  onSetAddress?: (e: React.MouseEvent) => void;
  onEditAddress?: (e: React.MouseEvent) => void;
}

// ─── Mobile Card ────────────────────────────────────────────────────────

const MobileCard: React.FC<CardInnerProps> = ({
  match, weather, weatherLoading, weatherIconData, hasWeather, showWeatherSection,
  hasLocation, hasConflict, conflictCount, conflictColor,
  hasScore, homeLost, awayLost, isPlayed, isInProgress, matchOver,
  fullAddress, leaveByStr, distanceStr, travelStr, borderColor,
  temperatureUnit, departureTimesLoading, departureTimesPending,
  formatMatchTime, onWeatherClick, onVenueClick, onConflictClick,
  onSetAddress, onEditAddress,
}) => {
  const showDash = isPlayed && !hasScore;
  return (
    <Box sx={{
      bgcolor: 'background.paper',
      borderRadius: '14px',
      border: '1px solid',
      borderColor: hasConflict ? (conflictColor ? `${conflictColor}33` : 'divider') : 'divider',
      ...(hasConflict && { borderLeft: `3px solid ${borderColor}` }),
      boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 2px 8px rgba(0,0,0,0.03)',
      overflow: 'hidden',
    }}>
      {/* League ribbon */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: '16px',
        py: '6px',
        bgcolor: 'action.hover',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <LeagueLogo
          leagueName={match.league?.name || ''}
          logoPath={match.league?.logoPath}
          logoUrl={match.league?.logoUrl}
          size={22}
        />
        <Typography noWrap sx={{ fontSize: '12px', fontWeight: 500, color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {match.league?.division || match.league?.name || ''}
        </Typography>
        {/* Status badges */}
        {isInProgress && <Chip label="Playing" size="small" color="info" variant="outlined" sx={{ height: 18, borderRadius: '4px', '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem' } }} />}
        {isPlayed && <Chip label="Played" size="small" color="success" variant="outlined" sx={{ height: 18, borderRadius: '4px', '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem', fontWeight: 600 } }} />}
        {match.status === 'CANCELLED' && <Chip label="Cancelled" size="small" variant="outlined" sx={{ height: 18, borderRadius: '4px', '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem' } }} />}
        {hasScore && match.scoreVerified === false && <Chip label="Unverified" size="small" color="warning" variant="outlined" sx={{ height: 18, borderRadius: '4px', '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem' } }} />}
      </Box>

      {/* Card body */}
      <Box sx={{ p: '16px 16px 14px' }}>
        {/* Section 1: Time + Weather Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '14px' }}>
          {/* Left: Time area (tappable when conflicts exist) */}
          <Box
            onClick={hasConflict ? onConflictClick : undefined}
            sx={{
              userSelect: 'none',
              ...(hasConflict && { cursor: 'pointer', WebkitTapHighlightColor: 'transparent', '&:active': { opacity: 0.7 } }),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography sx={{ fontSize: '22px', fontWeight: 800, color: 'text.primary', lineHeight: 1, whiteSpace: 'nowrap' }}>
                  {formatMatchTime(match.dateTime, match.status, match)}
                </Typography>
                {/* Timezone — inline next to time */}
                {match.dateTime && (
                  <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                    {match.timezoneVerified === false ? 'Venue time' : formatInUserTimezone(match.dateTime, 'z', false)}
                  </Typography>
                )}
              </Box>
              {/* Status badges */}
              {match.timezoneVerified === false && match.dateTime && (
                <Chip label="Venue Time" size="small" color="info" sx={{ height: 20, borderRadius: '4px', '& .MuiChip-label': { px: 1, fontSize: '0.65rem', fontWeight: 600 } }} />
              )}
              {match.recentChange && (
                <Chip
                  label={match.recentChange.badge === 'scheduled' ? 'Scheduled' : match.recentChange.badge === 'rescheduled' ? 'Rescheduled' : match.recentChange.badge === 'new' ? 'New' : 'Moved'}
                  size="small"
                  color={match.recentChange.badge === 'scheduled' ? 'success' : match.recentChange.badge === 'new' ? 'info' : match.recentChange.badge === 'moved' ? 'info' : 'warning'}
                  sx={{ height: 20, borderRadius: '4px', '& .MuiChip-label': { px: 1, fontSize: '0.65rem', fontWeight: 600 } }}
                />
              )}
            </Box>
            {/* Conflict indicator text */}
            {hasConflict && conflictColor && (
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: conflictColor, mt: '3px' }}>
                {conflictCount === 1 ? '1 conflict' : `${conflictCount} conflicts`} ›
              </Typography>
            )}
          </Box>

          {/* Right: Glass weather pill */}
          {showWeatherSection && (
            <GlassWeatherPill
              weather={weather}
              weatherIconData={weatherIconData}
              hasWeather={hasWeather}
              loading={weatherLoading}
              temperatureUnit={temperatureUnit}
              onClick={onWeatherClick}
            />
          )}
        </Box>

        {/* Section 2: Teams */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: '14px' }}>
          <TeamRow
            teamName={match.homeTeam}
            logoPath={match.homeTeamLogo?.logoPath}
            logoUrl={match.homeTeamLogo?.logoUrl}
            tag="HOME"
            score={hasScore ? match.homeScore : undefined}
            dimmed={homeLost}
            size="mobile"
            showDash={showDash}
          />
          <TeamRow
            teamName={match.awayTeam}
            logoPath={match.awayTeamLogo?.logoPath}
            logoUrl={match.awayTeamLogo?.logoUrl}
            tag="AWAY"
            score={hasScore ? match.awayScore : undefined}
            dimmed={awayLost}
            size="mobile"
            showDash={showDash}
          />
        </Box>

        {/* Divider */}
        <Box sx={{ height: '1px', bgcolor: 'divider', mt: '14px', mb: '14px' }} />

        {/* Section 3: Venue (tappable) */}
        <Box
          onClick={hasLocation ? onVenueClick : undefined}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: '12px',
            cursor: hasLocation ? 'pointer' : 'default',
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none',
            '&:active': hasLocation ? { opacity: 0.7 } : {},
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Venue name */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
              <Box sx={{ mt: '2px', flexShrink: 0, display: 'flex' }}><VenueIcon match={match} size={16} /></Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: hasLocation ? 'primary.main' : 'text.primary', lineHeight: 1.3 }}>
                {match.location ? <VenueName name={match.location} /> : 'TBD'}
              </Typography>
              {!match.locationConfirmed && match.clubLocationFallback && (
                <Chip label="Temp" size="small" color="warning" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' } }} />
              )}
            </Box>
            {/* Address */}
            {fullAddress && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '2px' }}>
                <Explore sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  {fullAddress}
                </Typography>
              </Box>
            )}
            {/* Travel line */}
            {!matchOver && (
              <TravelLine
                leaveByStr={leaveByStr}
                distanceStr={distanceStr}
                travelStr={travelStr}
                loading={departureTimesLoading}
                pending={departureTimesPending}
                hasPlayers={!!match.players?.length}
                hasDateTime={!!match.dateTime}
              />
            )}
            {/* Set/Edit address link */}
            {!hasLocation && match.location && match.location !== 'TBD' && !match.venueOverride && onSetAddress && (
              <SetAddressLink onClick={onSetAddress} label="Set Address" />
            )}
            {match.venueOverride && onEditAddress && (
              <SetAddressLink onClick={onEditAddress} label="Edit Address" isEdit />
            )}
          </Box>
        </Box>

        {/* Players */}
        {match.players && match.players.length > 0 && (
          <Box sx={{ mt: '16px' }}>
            <PlayerRow match={match} size="mobile" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Desktop Card ───────────────────────────────────────────────────────

const DesktopCard: React.FC<CardInnerProps> = ({
  match, weather, weatherLoading, weatherIconData, hasWeather, showWeatherSection,
  hasLocation, hasConflict, conflictCount, conflictColor,
  hasScore, homeLost, awayLost, isPlayed, isInProgress, matchOver,
  fullAddress, leaveByStr, distanceStr, travelStr, borderColor,
  temperatureUnit, departureTimesLoading, departureTimesPending,
  formatMatchTime, onWeatherClick, onVenueClick, onConflictClick,
  onSetAddress, onEditAddress,
}) => {
  const showDash = isPlayed && !hasScore;
  return (
    <Box sx={{
      bgcolor: 'background.paper',
      borderRadius: '12px',
      border: '1px solid',
      borderColor: hasConflict ? (conflictColor ? `${conflictColor}33` : 'divider') : 'divider',
      ...(hasConflict && { borderLeft: `3px solid ${borderColor}` }),
      boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 2px 8px rgba(0,0,0,0.03)',
      overflow: 'hidden',
    }}>
      {/* League ribbon */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: '14px',
        py: '5px',
        bgcolor: 'action.hover',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <LeagueLogo
          leagueName={match.league?.name || ''}
          logoPath={match.league?.logoPath}
          logoUrl={match.league?.logoUrl}
          size={24}
        />
        <Typography noWrap sx={{ fontSize: '11px', fontWeight: 500, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {match.league?.division || match.league?.name || ''}
        </Typography>
      </Box>

      {/* 4-column layout */}
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
      {/* Column 1: Time — tappable when conflicts exist */}
      <Box
        onClick={hasConflict ? onConflictClick : undefined}
        sx={{
          width: 150,
          flexShrink: 0,
          bgcolor: 'action.hover',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          p: '16px 12px',
          userSelect: 'none',
          ...(hasConflict && { cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontSize: '24px', fontWeight: 800, color: 'text.primary', whiteSpace: 'nowrap', lineHeight: 1 }}>
            {formatMatchTime(match.dateTime, match.status, match)}
          </Typography>
          {match.dateTime && (
            <Typography sx={{ fontSize: '10px', color: 'text.disabled', whiteSpace: 'nowrap' }}>
              {match.timezoneVerified === false ? 'Venue time' : formatInUserTimezone(match.dateTime, 'z', false)}
            </Typography>
          )}
        </Box>
        {/* Status badges */}
        {(isInProgress || isPlayed || match.status === 'CANCELLED') && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: '5px', flexWrap: 'wrap' }}>
            {isInProgress && <Chip label="Playing" size="small" color="info" variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem' } }} />}
            {isPlayed && <Chip label="Played" size="small" color="success" variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem', fontWeight: 600 } }} />}
            {match.status === 'CANCELLED' && <Chip label="Cancelled" size="small" variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem' } }} />}
            {hasScore && match.scoreVerified === false && <Chip label="Unverified" size="small" color="warning" variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem' } }} />}
          </Box>
        )}
        {match.recentChange && (
          <Chip
            label={match.recentChange.badge === 'scheduled' ? 'Scheduled' : match.recentChange.badge === 'rescheduled' ? 'Rescheduled' : match.recentChange.badge === 'new' ? 'New' : 'Moved'}
            size="small"
            color={match.recentChange.badge === 'scheduled' ? 'success' : match.recentChange.badge === 'new' ? 'info' : match.recentChange.badge === 'moved' ? 'info' : 'warning'}
            sx={{ mt: 0.5, height: 20, borderRadius: '4px', '& .MuiChip-label': { px: 1, fontSize: '0.6rem', fontWeight: 600 } }}
          />
        )}
        {match.timezoneVerified === false && match.dateTime && (
          <Chip label="Venue" size="small" color="info" sx={{ mt: 0.5, height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem', fontWeight: 600 } }} />
        )}
        {/* Conflict text indicator */}
        {hasConflict && conflictColor && (
          <Typography sx={{ fontSize: '12px', fontWeight: 500, color: conflictColor, mt: '5px' }}>
            {conflictCount === 1 ? '1 conflict' : `${conflictCount} conflicts`} ›
          </Typography>
        )}
      </Box>

      {/* Column 2: Matchup (flex: 1) */}
      <Box sx={{
        flex: 1,
        p: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '7px',
        minWidth: 0,
      }}>
        <TeamRow
          teamName={match.homeTeam}
          logoPath={match.homeTeamLogo?.logoPath}
          logoUrl={match.homeTeamLogo?.logoUrl}
          tag="HOME"
          score={hasScore ? match.homeScore : undefined}
          dimmed={homeLost}
          size="desktop"
          showDash={showDash}
        />
        <TeamRow
          teamName={match.awayTeam}
          logoPath={match.awayTeamLogo?.logoPath}
          logoUrl={match.awayTeamLogo?.logoUrl}
          tag="AWAY"
          score={hasScore ? match.awayScore : undefined}
          dimmed={awayLost}
          size="desktop"
          showDash={showDash}
        />
        {/* Player row */}
        {match.players && match.players.length > 0 && (
          <>
            <Box sx={{ height: '1px', bgcolor: 'divider', mt: '6px' }} />
            <Box sx={{ mt: '8px' }}>
              <PlayerRow match={match} size="desktop" />
            </Box>
          </>
        )}
      </Box>

      {/* Column 3: Venue (tappable) */}
      <Box
        onClick={hasLocation ? onVenueClick : undefined}
        sx={{
          width: 470,
          flexShrink: 0,
          borderLeft: '1px solid',
          borderColor: 'divider',
          p: '14px 18px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          gap: '3px',
          cursor: hasLocation ? 'pointer' : 'default',
          userSelect: 'none',
          transition: 'background-color 0.15s',
          '&:hover': hasLocation ? { bgcolor: 'action.hover' } : {},
        }}
      >
        {/* Venue name */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
          <Box sx={{ mt: '2px', flexShrink: 0, display: 'flex' }}><VenueIcon match={match} size={14} /></Box>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: hasLocation ? 'primary.main' : 'text.primary', lineHeight: 1.3 }}>
            {match.location ? <VenueName name={match.location} /> : 'TBD'}
          </Typography>
        </Box>
        {/* Address */}
        {fullAddress && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Explore sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '13px', color: 'text.secondary' }}>
              {fullAddress}
            </Typography>
          </Box>
        )}
        {/* Travel line */}
        {!matchOver && (
          <TravelLine
            leaveByStr={leaveByStr}
            distanceStr={distanceStr}
            travelStr={travelStr}
            loading={departureTimesLoading}
            pending={departureTimesPending}
            hasPlayers={!!match.players?.length}
            hasDateTime={!!match.dateTime}
            compact
          />
        )}
        {/* Set/Edit address */}
        {!hasLocation && match.location && match.location !== 'TBD' && !match.venueOverride && onSetAddress && (
          <SetAddressLink onClick={onSetAddress} label="Set Address" />
        )}
        {match.venueOverride && onEditAddress && (
          <SetAddressLink onClick={onEditAddress} label="Edit Address" isEdit />
        )}
      </Box>

      {/* Column 4: Weather or Mini Map (160px) */}
      {showWeatherSection && (hasWeather || weatherLoading) ? (
        <Box
          onClick={hasWeather ? onWeatherClick : undefined}
          sx={{
            width: 160,
            flexShrink: 0,
            borderLeft: '1px solid',
            borderColor: 'divider',
            p: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: hasWeather ? 'pointer' : 'default',
            userSelect: 'none',
            transition: 'background-color 0.15s',
            '&:hover': hasWeather ? { bgcolor: 'action.hover' } : {},
          }}
        >
          {weatherLoading && !weather && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton variant="text" width={50} height={20} />
              <Skeleton variant="text" width={80} height={14} />
            </Box>
          )}
          {hasWeather && weatherIconData && (
            <>
              {/* Icon + Temp row */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                <img src={weatherIconData.src} width={44} height={44} alt="" style={{ margin: '-10px', marginBottom: '-6px' }} />
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: 'text.primary' }}>
                  {weather!.temperature != null ? `${convertTemp(weather!.temperature, temperatureUnit)}${tempUnitLabel(temperatureUnit)}` : ''}
                </Typography>
              </Box>
              {/* Forecast */}
              {weather!.shortForecast && (
                <Typography noWrap sx={{ fontSize: '13px', color: 'text.secondary', textAlign: 'center', mt: '3px', maxWidth: '100%' }}>
                  {weather!.shortForecast}
                </Typography>
              )}
              {/* Humidity / AQI */}
              {(weather!.humidity != null || weather!.aqi != null) && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: '3px' }}>
                  {weather!.humidity != null && (
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>💧 {weather!.humidity}%</Typography>
                  )}
                  {weather!.humidity != null && weather!.aqi != null && (
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>·</Typography>
                  )}
                  {weather!.aqi != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: getAqiColor(weather!.aqi) }} />
                      <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>AQI {weather!.aqi}</Typography>
                    </Box>
                  )}
                </Box>
              )}
              {/* Details link */}
              <Typography sx={{ fontSize: '10px', color: 'primary.main', fontWeight: 600, mt: '4px' }}>
                Details ›
              </Typography>
            </>
          )}
        </Box>
      ) : match.latitude && match.longitude ? (() => {
        const firstPlayer = (match.players || []).find(p =>
          p.homeLatitude && p.homeLongitude &&
          !(Math.abs(p.homeLatitude! - match.latitude!) < 0.001 && Math.abs(p.homeLongitude! - match.longitude!) < 0.001)
        );
        const miniMapMarkers = firstPlayer ? [{
          latitude: firstPlayer.homeLatitude!,
          longitude: firstPlayer.homeLongitude!,
          type: 'home' as const,
          color: 'info.main',
        }] : [];
        return (
          <Box
            onClick={hasLocation ? onVenueClick : undefined}
            sx={{
              width: 160,
              flexShrink: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
              cursor: hasLocation ? 'pointer' : 'default',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <InlineMapPreview
              latitude={match.latitude}
              longitude={match.longitude}
              height="100%"
              width="100%"
              markers={miniMapMarkers}
              markerScale={0.55}
            />
          </Box>
        );
      })() : (
        <Box sx={{ width: 160, flexShrink: 0 }} />
      )}
      </Box>{/* end 4-column layout */}
    </Box>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────────

const TeamRow: React.FC<{
  teamName: string;
  logoPath?: string;
  logoUrl?: string;
  tag: 'HOME' | 'AWAY';
  score?: number | null;
  dimmed?: boolean;
  size: 'mobile' | 'desktop';
  showDash?: boolean;
}> = ({ teamName, logoPath, logoUrl, tag, score, dimmed, size, showDash }) => {
  const logoSize = size === 'mobile' ? 30 : 30;
  const nameSize = size === 'mobile' ? '14px' : '15px';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: size === 'mobile' ? 1 : '10px', opacity: dimmed ? 0.45 : 1 }}>
      <TeamLogo
        teamName={teamName}
        size={logoSize}
        logoPath={logoPath}
        logoUrl={logoUrl}
      />
      <Typography sx={{
        fontSize: nameSize,
        fontWeight: 600,
        color: 'text.primary',
        lineHeight: 1.3,
        ...(size === 'mobile' ? { flex: 1 } : {}),
        minWidth: 0,
        // Allow wrapping to 2 lines
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {teamName}
      </Typography>
      <Typography sx={{
        fontSize: score != null ? (size === 'mobile' ? '18px' : '20px') : showDash ? (size === 'mobile' ? '18px' : '20px') : '11px',
        fontWeight: score != null ? 800 : showDash ? 800 : 700,
        color: score != null ? 'text.primary' : showDash ? 'text.secondary' : 'text.disabled',
        letterSpacing: score != null || showDash ? undefined : '0.04em',
        flexShrink: 0,
        ...(score != null || showDash ? { ml: 'auto', minWidth: 28, textAlign: 'center' } : {}),
        ...(size === 'desktop' && score == null && !showDash ? { ml: 0.5, alignSelf: 'center' } : {}),
      }}>
        {score != null ? score : showDash ? '-' : tag}
      </Typography>
    </Box>
  );
};

const PlayerRow: React.FC<{ match: ScheduleMatch; size: 'mobile' | 'desktop' }> = ({ match, size }) => {
  const avatarSize = size === 'mobile' ? 22 : 20;
  const ps = match.players || [];
  if (ps.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      {ps.map((p) => (
        <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PlayerAvatar
            playerId={p.id}
            player={p}
            size="small"
            fallbackBgColor={p.playerColor}
            sx={{
              width: avatarSize,
              height: avatarSize,
              flexShrink: 0,
              fontSize: '0.55rem',
            }}
          />
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>
            {p.name}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};


const GlassWeatherPill: React.FC<{
  weather?: MatchWeatherData;
  weatherIconData: { src: string } | null;
  hasWeather: boolean;
  loading?: boolean;
  temperatureUnit: 'FAHRENHEIT' | 'CELSIUS';
  onClick: (e: React.MouseEvent) => void;
}> = ({ weather, weatherIconData, hasWeather, loading, temperatureUnit, onClick }) => {
  if (loading && !weather) {
    return (
      <Skeleton variant="rounded" width={80} height={40} sx={{ borderRadius: '10px', mt: '-6px' }} />
    );
  }
  if (!hasWeather || !weatherIconData || !weather) return null;

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        '&:active': { opacity: 0.85 },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
        <img src={weatherIconData.src} width={36} height={36} alt="" style={{ marginTop: '-8px', marginBottom: '-8px' }} />
        <Typography sx={{ fontSize: '22px', fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>
          {weather.temperature != null ? `${convertTemp(weather.temperature, temperatureUnit)}${tempUnitLabel(temperatureUnit)}` : ''}
        </Typography>
      </Box>
      {weather.shortForecast && (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: '3px', textAlign: 'right', userSelect: 'none' }}>
          {weather.shortForecast}
        </Typography>
      )}
    </Box>
  );
};

const TravelLine: React.FC<{
  leaveByStr: string | null;
  distanceStr: string | null;
  travelStr: string | null;
  loading: boolean;
  pending: boolean;
  hasPlayers: boolean;
  hasDateTime: boolean;
  compact?: boolean;
}> = ({ leaveByStr, distanceStr, travelStr, loading, pending, hasPlayers, hasDateTime, compact }) => {
  if (!hasPlayers || !hasDateTime) return null;
  if (loading) return <Skeleton variant="text" width={compact ? 140 : 180} height={14} sx={{ mt: '3px' }} />;
  if (!leaveByStr && !travelStr) {
    if (pending) return <Skeleton variant="text" width={compact ? 140 : 180} height={14} sx={{ mt: '3px' }} />;
    return null;
  }

  const parts: React.ReactNode[] = [];
  if (leaveByStr) {
    parts.push(
      <Typography key="leave" component="span" sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }}>
        Leave {leaveByStr}
      </Typography>
    );
  }
  if (distanceStr) {
    parts.push(<Typography key="dist" component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>{distanceStr}</Typography>);
  }
  if (travelStr) {
    parts.push(<Typography key="travel" component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>{travelStr}</Typography>);
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '3px' }}>
      <DirectionsCar sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
        {parts.map((p, i) => (
          <React.Fragment key={i}>
            {i > 0 && <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>·</Typography>}
            {p}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

/** Renders venue name so each segment around " - " is non-breaking,
 *  forcing the browser to prefer wrapping at the dash when needed. */
const VenueName: React.FC<{ name: string }> = ({ name }) => {
  const parts = name.split(/\s-\s/);
  if (parts.length <= 1) return <>{name}</>;
  return (
    <>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <> &#8209; </>}
          <span style={{ whiteSpace: 'nowrap' }}>{part}</span>
        </React.Fragment>
      ))}
    </>
  );
};

const VenueIcon: React.FC<{ match: ScheduleMatch; size: number }> = ({ match, size }) => {
  if (match.venueOverride) {
    return <EditLocation sx={{ fontSize: size, color: 'success.main', flexShrink: 0 }} />;
  }
  if (match.latitude && match.longitude) {
    return <Directions sx={{ fontSize: size, color: 'primary.main', flexShrink: 0 }} />;
  }
  return <Place sx={{ fontSize: size, color: 'text.secondary', flexShrink: 0 }} />;
};

const SetAddressLink: React.FC<{ onClick: (e: React.MouseEvent) => void; label: string; isEdit?: boolean }> = ({ onClick, label, isEdit }) => (
  <Box
    component="span"
    onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onClick(e); }}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      mt: 0.5,
      px: 1,
      py: 0.25,
      borderRadius: '12px',
      border: '1.5px solid',
      borderColor: 'text.secondary',
      color: 'text.secondary',
      fontSize: '0.7rem',
      fontWeight: 500,
      cursor: 'pointer',
      '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
    }}
  >
    {isEdit ? <EditLocation sx={{ fontSize: 12 }} /> : <Place sx={{ fontSize: 12 }} />}
    {label}
  </Box>
);

export default ScheduleMatchCard;
